import { useEffect, useRef, useState } from 'react';
import { History, X, RotateCcw, Clock } from 'lucide-react';
import { nanoid } from 'nanoid';
import { api } from '../lib/api';
import { useVault } from '../../../core/lib/vault';
import { useCanAdminister } from '../lib/usePermissions';
import { decryptJson, encryptJson } from '../../../core/crypto';
import { getDecryptedDocument, pullAll, type DocumentPlain } from '../sync';
import { DocumentPreviewPane } from './DocumentPreviewPane';

interface SnapshotEntry {
  id: string;
  authorId: string;
  createdAt: string;
  plain: DocumentPlain | null;
}

const PAGE_SIZE = 50;

/**
 * Read-only browsable timeline of a document's snapshots. Restoring produces
 * a **new** "Recovered" document (sibling of the original) — the original is
 * never touched, so restore is entirely additive.
 *
 * Named VersionHistoryDrawer for call-site compatibility; user-facing labels
 * now say "Snapshots".
 */
export function VersionHistoryDrawer({
  documentId,
  onClose,
  onRestored,
}: {
  documentId: string;
  onClose: () => void;
  onRestored?: (newDocId: string) => void;
}) {
  const vaultKey = useVault((s) => s.vaultKey);
  const userId = useVault((s) => s.user?.id);
  // All vault members can open the drawer and browse snapshots — "Restore
  // as Recovered page" is admin-only per the permission model (editors can
  // still edit the live document freely; restoring a historical snapshot is
  // reserved so the history chain isn't casually overwritten).
  const canAdminister = useCanAdminister();
  const [snapshots, setSnapshots] = useState<SnapshotEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!vaultKey) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.getVersions(documentId, { limit: PAGE_SIZE });
        const decrypted: SnapshotEntry[] = [];
        for (const v of res.versions) {
          let plain: DocumentPlain | null = null;
          try {
            plain = await decryptJson<DocumentPlain>(v.payload, vaultKey);
          } catch {
            /* undecryptable */
          }
          decrypted.push({ id: v.id, authorId: v.authorId, createdAt: v.createdAt, plain });
        }
        if (cancelled) return;
        setSnapshots(decrypted);
        setNextBefore(res.nextBefore);
        if (decrypted[0]) setSelectedId(decrypted[0].id);
      } catch (err: any) {
        if (!cancelled) setError(err?.body?.error ?? err?.message ?? 'Could not load snapshots.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [documentId, vaultKey]);

  async function loadMore() {
    if (!vaultKey || !nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.getVersions(documentId, { before: nextBefore, limit: PAGE_SIZE });
      const decrypted: SnapshotEntry[] = [];
      for (const v of res.versions) {
        let plain: DocumentPlain | null = null;
        try {
          plain = await decryptJson<DocumentPlain>(v.payload, vaultKey);
        } catch {
          /* undecryptable */
        }
        decrypted.push({ id: v.id, authorId: v.authorId, createdAt: v.createdAt, plain });
      }
      setSnapshots((prev) => [...prev, ...decrypted]);
      setNextBefore(res.nextBefore);
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Could not load more snapshots.');
    } finally {
      setLoadingMore(false);
    }
  }

  function onScroll() {
    const el = scrollerRef.current;
    if (!el || !nextBefore || loadingMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      void loadMore();
    }
  }

  const selected = snapshots.find((v) => v.id === selectedId) ?? null;

  async function restoreAsRecovered() {
    if (!selected?.plain || !vaultKey) return;
    if (
      !confirm(
        'Restore this snapshot as a NEW "Recovered" page alongside the original? Nothing is overwritten — the recovered copy can be deleted/moved independently.',
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const current = await getDecryptedDocument(documentId, vaultKey);
      if (!current) throw new Error('Current document not found.');
      const recoveredPlain: DocumentPlain = {
        ...selected.plain,
        title: `Recovered: ${selected.plain.title || 'Untitled'}`,
      };
      const payload = await encryptJson(recoveredPlain, vaultKey);
      const newId = nanoid();
      await api.createDocument({
        id: newId,
        customerId: current.customerId,
        parentId: current.parentId,
        payload,
        recoveredFromVersionId: selected.id,
      });
      // Refresh local cache so the sidebar/tab strip picks up the new doc.
      await pullAll();
      onRestored?.(newId);
      onClose();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Restore failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="card w-full max-w-4xl h-[100dvh] md:h-[80vh] flex flex-col">
        <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-3">
          <History size={18} className="text-dragon-500" />
          <h2 className="font-semibold">Snapshots</h2>
          <span className="text-xs text-ink-500 ml-2">
            {loading ? 'Loading…' : `${snapshots.length}${nextBefore ? '+' : ''} snapshot${snapshots.length === 1 ? '' : 's'}`}
          </span>
          <button onClick={onClose} className="btn-ghost ml-auto !p-1">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <aside
            ref={scrollerRef}
            onScroll={onScroll}
            className="w-full md:w-72 max-h-[40%] md:max-h-none border-b md:border-b-0 md:border-r border-ink-800 overflow-y-auto"
          >
            {snapshots.length === 0 && !loading && (
              <p className="p-4 text-xs text-ink-500">No snapshots yet — start editing to create one.</p>
            )}
            <ul>
              {snapshots.map((v, idx) => {
                const selectedCls = v.id === selectedId ? 'bg-ink-800' : 'hover:bg-ink-800/60';
                const isCurrent = idx === 0;
                return (
                  <li key={v.id}>
                    <button
                      onClick={() => setSelectedId(v.id)}
                      className={`w-full text-left px-4 py-3 border-b border-ink-800/60 ${selectedCls}`}
                    >
                      <div className="flex items-center gap-2">
                        <Clock size={12} className="text-ink-500" />
                        <span className="text-sm">{formatTime(v.createdAt)}</span>
                        {isCurrent && (
                          <span className="ml-auto text-[10px] uppercase tracking-wider text-dragon-500">
                            current
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-ink-500 truncate">
                        {v.authorId === userId ? 'you' : shortId(v.authorId)}
                        {v.plain?.title ? ` · ${v.plain.title}` : ''}
                      </div>
                    </button>
                  </li>
                );
              })}
              {loadingMore && (
                <li className="p-3 text-center text-xs text-ink-500">Loading more…</li>
              )}
            </ul>
          </aside>

          <section className="flex-1 min-w-0 overflow-y-auto">
            {selected ? (
              selected.plain ? (
                <article className="p-6">
                  <h3 className="text-lg font-semibold mb-1">
                    {selected.plain.title || '(untitled)'}
                  </h3>
                  <p className="text-xs text-ink-500 mb-4">
                    {formatTime(selected.createdAt)} ·{' '}
                    {selected.authorId === userId ? 'you' : shortId(selected.authorId)}
                  </p>
                  <DocumentPreviewPane plain={selected.plain} />
                </article>
              ) : (
                <p className="p-6 text-sm text-ink-500">
                  This snapshot could not be decrypted with the current vault key.
                </p>
              )
            ) : (
              <p className="p-6 text-sm text-ink-500">Select a snapshot to preview.</p>
            )}
          </section>
        </div>

        {error && (
          <div className="border-t border-red-900 bg-red-950/40 px-4 py-2 text-xs text-red-300">
            {error}
          </div>
        )}
        <div className="flex items-center gap-2 border-t border-ink-800 px-4 py-3">
          <span className="text-xs text-ink-500">
            {canAdminister
              ? 'Restore creates a new "Recovered" page — the original is never overwritten.'
              : 'Snapshots are view-only — only a vault admin can restore an old version.'}
          </span>
          {canAdminister && (
            <button
              className="btn-primary ml-auto"
              disabled={!selected?.plain || busy}
              onClick={restoreAsRecovered}
            >
              <RotateCcw size={14} className="mr-1" />
              {busy ? 'Restoring…' : 'Restore as Recovered page'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}
